import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Menu,
  MenuItem,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ManagetNav from '../../components/ManagerNav/ManagerNav';
import ProductApi from '../../api/ProductApi';
import { ProductDto, CreateProductDto } from '../../interfaces/ProductInterfaces';
import { licenseTypeCodeToLabel, PRODUCT_LICENSE_TYPE_OPTIONS, ProductLicenseTypeCode } from '../../utils/productLicenseType';
import './ManagerProductsView.scss';

interface SearchFilters {
  search: string;
}

const initialFilters: SearchFilters = {
  search: ''
};

const ManagerProductsView: React.FC = () => {
  const navigate = useNavigate();
  
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SearchFilters>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentFrom, setCurrentFrom] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  
  // Create product dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFormData, setCreateFormData] = useState<CreateProductDto>({
    Product_Name: '',
    Product_LicenseType: 'class_c',
    Product_Desc: '',
    Product_Cost: 0
  });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Actions menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductDto | null>(null);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const RESULTS_PER_PAGE = 20;

  const fetchProducts = useCallback(async (searchFilters = filters, from = 0, appendResults = false) => {
    if (!appendResults) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);

    try {
      const to = from + RESULTS_PER_PAGE;
      const result = await ProductApi.getProducts({
        from,
        to,
        search: searchFilters.search.trim() || undefined
      });
      
      if (appendResults) {
        setProducts(prevProducts => [...prevProducts, ...result.data]);
      } else {
        setProducts(result.data);
      }
      
      // Check if there are more results
      setHasMoreData(result.pagination.hasMore);
      setCurrentFrom(from);
      setHasSearched(true);
      
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [RESULTS_PER_PAGE]);

  useEffect(() => {
    fetchProducts(initialFilters, 0, false);
  }, [fetchProducts]);

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleSearch = () => {
    setCurrentFrom(0);
    setHasMoreData(false);
    fetchProducts(filters, 0, false);
  };

  const handleLoadMore = () => {
    const nextFrom = currentFrom + RESULTS_PER_PAGE;
    fetchProducts(filters, nextFrom, true);
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    setCurrentFrom(0);
    setHasMoreData(false);
    setError(null);
    fetchProducts(initialFilters, 0, false);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleCreateProduct = async () => {
    setCreating(true);
    setCreateError(null);

    try {
      await ProductApi.createProduct(createFormData);
      setCreateDialogOpen(false);
      setCreateFormData({
        Product_Name: '',
        Product_LicenseType: 'class_c',
        Product_Desc: '',
        Product_Cost: 0
      });
      // Refresh the products list
      fetchProducts(filters, 0, false);
    } catch (err) {
      console.error('Error creating product:', err);
      setCreateError(err instanceof Error ? err.message : 'Failed to create product');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFormChange = (field: keyof CreateProductDto, value: string | number) => {
    setCreateFormData({ ...createFormData, [field]: value });
  };

  const formatCurrency = (cost: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cost / 100);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, product: ProductDto) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedProduct(null);
  };

  const handleEdit = () => {
    if (selectedProduct) {
      navigate(`/product?Product_guid=${selectedProduct.Product_guid}`);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    // Don't close the menu yet - keep selectedProduct available
    setMenuAnchorEl(null); // Just close the menu visual, but keep selectedProduct
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return;

    setDeleting(true);
    try {
      await ProductApi.deleteProduct(selectedProduct.Product_guid);
      // Remove the deleted product from the list
      setProducts(products.filter(p => p.Product_guid !== selectedProduct.Product_guid));
      setDeleteDialogOpen(false);
      setSelectedProduct(null);
    } catch (err) {
      console.error('Error deleting product:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedProduct(null);
  };

  return (
    <>
      <ManagetNav />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Manage Products
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            color="primary"
          >
            Create Product
          </Button>
        </Box>

        {/* Search Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Products
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField
              label="Search by name or description"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyDown={handleKeyDown}
              variant="outlined"
              size="small"
              sx={{ minWidth: 300, flex: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              size="small"
              disabled={loading}
            >
              Search Products
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              size="small"
            >
              Clear Filters
            </Button>
          </Box>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Results */}
        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {hasSearched && (
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" color="textSecondary">
                    {products.length === 0 ? 'No products found' : `${products.length} product${products.length === 1 ? '' : 's'} found`}
                  </Typography>
                </Box>
              )}
              
              {products.length === 0 && hasSearched ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    No products found matching your search criteria.
                  </Typography>
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Product Name</TableCell>
                          <TableCell>License Type</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell>Cost</TableCell>
                          <TableCell>Created</TableCell>
                          <TableCell>Updated</TableCell>
                          <TableCell align="center" sx={{ width: 80 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {products.map((product) => (
                          <TableRow
                            key={product.Product_guid}
                            sx={{
                              '&:hover': {
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {product.Product_Name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {product.Product_LicenseType ? (
                                <Chip
                                  label={licenseTypeCodeToLabel(product.Product_LicenseType)}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : (
                                <Chip label="Not set" size="small" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {product.Product_Desc || 'No description'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium" color="primary">
                                {formatCurrency(product.Product_Cost)}
                              </Typography>
                            </TableCell>
                            <TableCell>{formatDate(product.Product_CreatedAt.toString())}</TableCell>
                            <TableCell>{formatDate(product.Product_UpdatedAt.toString())}</TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                onClick={(event) => handleMenuOpen(event, product)}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Load More Button */}
                  {hasMoreData && (
                    <Box sx={{ p: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        startIcon={loadingMore ? <CircularProgress size={20} /> : undefined}
                      >
                        {loadingMore ? 'Loading...' : `Load More (Showing ${products.length} results)`}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </>
          )}
        </Paper>

        {/* Create Product Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Product</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter the details for the new product. Cost should be entered in cents (e.g., 29999 for $299.99).
            </DialogContentText>
            
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {createError}
              </Alert>
            )}

            <TextField
              autoFocus
              margin="dense"
              label="Product Name"
              type="text"
              fullWidth
              variant="outlined"
              value={createFormData.Product_Name}
              onChange={(e) => handleCreateFormChange('Product_Name', e.target.value)}
              sx={{ mb: 2 }}
              required
            />

            <FormControl fullWidth margin="dense" sx={{ mb: 2 }} required>
              <InputLabel id="create-product-license-type-label">License Type</InputLabel>
              <Select
                labelId="create-product-license-type-label"
                value={createFormData.Product_LicenseType}
                label="License Type"
                onChange={(e) => handleCreateFormChange('Product_LicenseType', e.target.value as ProductLicenseTypeCode)}
              >
                {PRODUCT_LICENSE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.code} value={option.code}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              margin="dense"
              label="Description"
              type="text"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={createFormData.Product_Desc}
              onChange={(e) => handleCreateFormChange('Product_Desc', e.target.value)}
              sx={{ mb: 2 }}
            />
            
            <TextField
              margin="dense"
              label="Cost (in cents)"
              type="number"
              fullWidth
              variant="outlined"
              value={createFormData.Product_Cost}
              onChange={(e) => handleCreateFormChange('Product_Cost', parseInt(e.target.value) || 0)}
              inputProps={{ min: 0 }}
              helperText={`Preview: ${formatCurrency(createFormData.Product_Cost)}`}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCreateDialogOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateProduct}
              variant="contained"
              disabled={creating || !createFormData.Product_Name.trim() || !createFormData.Product_LicenseType}
              startIcon={creating ? <CircularProgress size={20} /> : undefined}
            >
              {creating ? 'Creating...' : 'Create Product'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1, fontSize: 20 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
        >
          <DialogTitle>Delete Product</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this product? This action cannot be undone.
            </DialogContentText>
            {selectedProduct && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {selectedProduct.Product_Name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Cost: {formatCurrency(selectedProduct.Product_Cost)}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleDeleteCancel}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {deleting ? 'Deleting...' : 'Delete Product'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ManagerProductsView;